import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { getSpecies } from '../../services/speciesService';
import { useTenantSafe } from '../../context/TenantContext';

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
    zIndex: 50000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 12, maxWidth: 540, width: '100%',
    maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,.3)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
  },
  body: { padding: '16px 20px' },
  label: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 14, marginBottom: 4 },
  value: { fontSize: 14, color: '#1e293b' },
  link: { color: '#2563eb', fontSize: 13 },
};

const LINK_LABELS = {
  ontario:           'Ontario Species at Risk',
  ontarioFishOnline: 'Ontario Fish ON-Line',
  canadaSara:        'Canada SARA Registry',
  cosewic:           'COSEWIC Assessment',
  inaturalist:       'iNaturalist',
  gbif:              'GBIF',
  wikimedia:         'Wikimedia Commons',
};

export default function SpeciesInfoModal({ species, speciesId, onClose, onEdit }) {
  const tenant = useTenantSafe();
  const id = speciesId || species?._id;
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!id);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setLoaded(null);
    setError('');
    setLoading(!!id);
    if (id) {
      getSpecies(id)
        .then((response) => { if (current) setLoaded(response.data); })
        .catch((err) => { if (current) setError(err.response?.data?.message || 'Could not load species details.'); })
        .finally(() => { if (current) setLoading(false); });
    }
    return () => { current = false; };
  }, [id, retry]);
  const s = loaded || species;
  if (!s) return null;

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  const currentStatuses = (s.officialStatuses || []).filter((st) => st.isCurrent);
  const links = Object.entries(LINK_LABELS)
    .filter(([k]) => s.externalLinks?.[k])
    .map(([k, label]) => ({ label, url: s.externalLinks[k] }));
  if (s.externalLinks?.other) links.push(...s.externalLinks.other);

  const content = (
    <div style={S.overlay} onClick={handleOverlay}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-label={s.commonName}>
        {/* Header */}
        <div style={S.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{s.commonName}</div>
            {s.communityName && <div style={{ fontSize: 13, color: '#16a34a', fontStyle: 'italic' }}>{s.communityName}</div>}
          </div>
          <button onClick={onClose} aria-label="Close species details" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>

        <div style={S.body}>
          {loading && <p role="status">Loading species details...</p>}
          {error && <div role="alert">{error} <button onClick={() => setRetry((value) => value + 1)}>Retry</button></div>}
          <div style={S.label}>Species category</div>
          <div style={S.value}>{s.category || 'Not recorded'}</div>
          {s.harvestCategory && <><div style={S.label}>Default harvest category</div><div style={S.value}>{s.harvestCategory.replace(/_/g, ' ')}</div></>}
          {s.subType && <><div style={S.label}>Sub-type</div><div style={S.value}>{s.subType}</div></>}
          {s.isActive === false && <p>Archived - unavailable for new selections.</p>}
          {!loading && !error && ![s.shortDescription, s.habitat, s.seasonalNotes, s.stewardshipNote].some(Boolean) &&
            <p>Profile notes have not been added yet.</p>}
          {onEdit && <button type="button" disabled={loading || !!error} onClick={() => onEdit(s)}>Edit species</button>}
          {/* Image */}
          {s.image?.url && (
            <img
              src={s.image.url}
              alt={s.commonName}
              style={{ width: '100%', borderRadius: 8, marginBottom: 14, maxHeight: 200, objectFit: 'cover' }}
            />
          )}

          {/* Names */}
          {s.alternateNames?.length > 0 && (
            <>
              <div style={S.label}>Also known as</div>
              <div style={S.value}>{s.alternateNames.join(' · ')}</div>
            </>
          )}
          {s.scientificName && (
            <>
              <div style={S.label}>Scientific name</div>
              <div style={{ ...S.value, fontStyle: 'italic' }}>{s.scientificName}</div>
            </>
          )}

          {/* Ecology */}
          {s.shortDescription && (
            <>
              <div style={S.label}>About</div>
              <div style={S.value}>{s.shortDescription}</div>
            </>
          )}
          {s.habitat && (
            <>
              <div style={S.label}>Habitat</div>
              <div style={S.value}>{s.habitat}</div>
            </>
          )}
          {s.seasonalNotes && (
            <>
              <div style={S.label}>Seasonal notes</div>
              <div style={S.value}>{s.seasonalNotes}</div>
            </>
          )}
          {s.stewardshipNote && (
            <div style={{ marginTop: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '10px 14px', fontSize: 13, color: '#166534' }}>
              <strong>RSMIN stewardship note:</strong> {s.stewardshipNote}
            </div>
          )}

          {/* Conservation status */}
          {currentStatuses.length > 0 && (
            <>
              <div style={S.label}>Official conservation status</div>
              {currentStatuses.map((st, i) => (
                <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}>{st.jurisdiction}</span>
                  {' · '}
                  <span>{st.status}</span>
                  {st.sourceUrl && (
                    <a href={st.sourceUrl} target="_blank" rel="noreferrer" style={{ ...S.link, marginLeft: 6 }}>
                      Source ↗
                    </a>
                  )}
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Stewardship guidance reflects local RSMIN knowledge and does not by itself determine legal harvesting permissions.
              </div>
            </>
          )}

          {/* External links */}
          {links.length > 0 && (
            <>
              <div style={S.label}>Learn more</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer" style={S.link}>
                    {l.label} ↗
                  </a>
                ))}
              </div>
            </>
          )}

          {id && (
            <a
              href={`${tenant?.viewTenant?.slug ? `/t/${tenant.viewTenant.slug}` : ''}/species/${id}`}
              style={{ display: 'block', marginTop: 16, color: '#2563eb', fontSize: 13 }}
            >
              View full species profile →
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
